import React from 'react';

import { type ReportItem } from '@/hooks/useReporting';
import { type Job, JobStatus } from '@/types';
import { getJobStatus } from '@/utils/job';

interface ActiveExportJobsProps {
  jobs: Job[];
  reports?: ReportItem[];
}

/**
 * Active Export Jobs Component
 *
 * Displays real-time status of PlexTrac report export jobs.
 * Shows job progress with visual indicators (colors, animations).
 *
 * Job Status Mapping:
 * - Running/AsyncRunning: Blue pulsing indicator
 * - Queued/AsyncReturning: Gray indicator
 * - Pass: Green indicator
 * - Fail: Red indicator
 *
 * @param jobs - Array of active export jobs
 * @param reports - Optional report data for display names
 */
export const ActiveExportJobs: React.FC<ActiveExportJobsProps> = ({
  jobs,
  reports,
}) => {
  if (!jobs || jobs.length === 0) {
    return null;
  }

  return (
    <div className="border-chariot-stroke border-t p-4">
      <div className="mb-3 flex items-center gap-2">
        <h4 className="text-chariot-text-primary text-sm font-semibold">
          Active Exports
        </h4>
        <div className="text-chariot-text-secondary bg-chariot-background-tertiary rounded-full px-2 py-0.5 text-xs font-medium">
          {jobs.length}
        </div>
      </div>
      <div className="space-y-2">
        {jobs.map((job: Job) => {
          // Extract report ID from job DNS pattern
          const reportIdMatch = job.dns?.match(/plextrac-report-(\d+)-/);
          const reportId = reportIdMatch ? reportIdMatch[1] : null;
          const report = reportId
            ? reports?.find((r: ReportItem) => String(r.id) === reportId)
            : null;

          const status = getJobStatus(job);
          const isRunning =
            status === JobStatus.Running || status === JobStatus.AsyncRunning;
          const isQueued =
            status === JobStatus.Queued || status === JobStatus.AsyncReturning;
          const isPassed = status === JobStatus.Pass;
          const isFailed = status === JobStatus.Fail;

          const statusColor = isRunning
            ? 'blue'
            : isQueued
              ? 'gray'
              : isPassed
                ? 'green'
                : 'red';

          return (
            <div
              key={job.key}
              className="border-chariot-stroke bg-chariot-background-primary flex items-center justify-between rounded-md border p-3"
            >
              <div className="flex items-center gap-3">
                <div
                  className={`size-2 rounded-full ${
                    isRunning
                      ? 'animate-pulse bg-blue-400'
                      : isQueued
                        ? 'bg-gray-400'
                        : isPassed
                          ? 'bg-green-400'
                          : 'bg-red-400'
                  }`}
                />
                <div className="flex flex-col">
                  <span className="text-chariot-text-primary text-sm font-medium">
                    {report?.name ||
                      (reportId ? `Report ${reportId}` : 'PlexTrac Export')}
                  </span>
                  <span className="text-chariot-text-secondary text-xs">
                    {isRunning
                      ? 'Exporting...'
                      : isQueued
                        ? 'Queued'
                        : isPassed
                          ? 'Completed'
                          : 'Failed'}
                  </span>
                </div>
              </div>
              <div
                className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                  statusColor === 'blue'
                    ? 'bg-blue-500/20 text-blue-400'
                    : statusColor === 'gray'
                      ? 'bg-gray-500/20 text-gray-400'
                      : statusColor === 'green'
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-red-500/20 text-red-400'
                }`}
              >
                {isRunning
                  ? 'Running'
                  : isQueued
                    ? 'Queued'
                    : isPassed
                      ? 'Success'
                      : 'Failed'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
